import {
  EContextEvent,
  useEventContext,
} from '@att-bit/duc.utils-react.events-context';
import React from 'react';
import { ButtonSpinner } from '.';
import { useUniqueId } from '@att-bit/duc.utils.custom-useid';
export interface ButtonProps {
  /**  children (text/jsx) to be rendered in component  */
  children?: any;
  /** look of button, primary or secondary */
  variant?: 'primary' | 'secondary';
  /** size of the button */
  size?: 'small' | 'fullWidth' | 'large';
  /** show the embedded spinner */
  spinner: boolean;
  /** event to send to context on click */
  dispatchEvent?: EContextEvent;
  /** event data to send to context on click */
  dispatchData?: any;
  /** apply full width on mobile */
  mobileFullWidth: boolean;
  /** string of class names */
  className?: string;
  /** react onClick method - using this prevents default behavior */
  onClick?: (e) => void;
  /** react onKeyDown method - using this prevents default behavior */
  onKeyDown?: (e) => void;
}

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  | ButtonProps
  | React.ButtonHTMLAttributes<HTMLButtonElement>
  | React.AnchorHTMLAttributes<HTMLAnchorElement>
>(
  (
    {
      children = null,
      variant = 'primary',
      disabled = false,
      size = 'large',
      spinner = false,
      dispatchData,
      dispatchEvent,
      mobileFullWidth = false,
      className,
      onClick,
      onKeyDown,
      ...props
    }: ButtonProps &
      React.ButtonHTMLAttributes<HTMLButtonElement> &
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
    ref
  ) => {
    if (!children) {
      return null;
    }

    const [disabledState, setDisabledState] = React.useState(
      disabled || spinner || false
    );
    const [spinnerState, setSpinnerState] = React.useState(!!spinner);

    const { dispatchFactory, button } = useEventContext();

    React.useEffect(() => {
      setDisabledState(disabled || spinnerState);
    }, [disabled, spinnerState]);

    React.useEffect(() => {
      setSpinnerState(spinner);
    }, [spinner]);

    const classNames: string[] = [];
    className && classNames.push(className);
    variant === 'primary' && classNames.push('btn-primary');
    variant === 'secondary' && classNames.push('btn-secondary');
    size === 'small' && classNames.push('btn-small');
    size === 'fullWidth' && classNames.push('btn-full-width');
    mobileFullWidth && classNames.push('btn-full-width-sm');
    // size === 'large' => not needed, is default

    const Element: any = props.href ? 'a' : 'button';
    const buttonId = useUniqueId({disabled,variant,size,className,mobileFullWidth,spinner},Element, children) //props need to be pass for unique id 
    const defaultHandler = async (e) => {
      if (disabledState) {
        e.preventDefault();
        return;
      }
      button?.onClick?.(e);
      if (dispatchEvent) {
        if (dispatchData?.useSpinner) {
          setSpinnerState(true);
        }
        dispatchFactory?.({
          event: e,
          type: dispatchEvent,
          data: dispatchData,
        }).then((result) => {
          // can do something with the result TBD
          setSpinnerState(false);
        });
      }
    };

    return (
      <Element
        className={classNames.join(' ')}
        id={buttonId}
        disabled={disabledState}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
          } else {
            defaultHandler(e);
          }
        }}
        onKeyDown={(e: any) => {
          if (onKeyDown) {
            onKeyDown(e);
          } else {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
              defaultHandler(e);
            }
          }
        }}
        type={!props.type && props.href && 'button'}
        {...props}
        ref={ref}
      >
        {spinnerState && <ButtonSpinner />}
        {children}
      </Element>
    );
  }
);

Button.displayName = 'Button';
